import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/ui/page-header";
import { Trash2, Plus } from "lucide-react";
import { useScope } from "@/hooks/use-scope";
import {
  listReturnReasons, upsertReturnReason, deleteReturnReason,
  listReturnDispositions, upsertReturnDisposition, deleteReturnDisposition,
} from "@/lib/returns-config.functions";

export const Route = createFileRoute("/_authenticated/app/admin/returns-config")({
  component: ReturnsConfigPage,
});

function ReturnsConfigPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const fetchReasons = useServerFn(listReturnReasons);
  const saveReason = useServerFn(upsertReturnReason);
  const removeReason = useServerFn(deleteReturnReason);
  const fetchDisp = useServerFn(listReturnDispositions);
  const saveDisp = useServerFn(upsertReturnDisposition);
  const removeDisp = useServerFn(deleteReturnDisposition);

  const reasonsQ = useQuery({
    queryKey: ["return-reasons", tenantId],
    enabled: !!tenantId,
    queryFn: () => fetchReasons({ data: { tenantId: tenantId! } }),
  });
  const dispQ = useQuery({
    queryKey: ["return-dispositions", tenantId],
    enabled: !!tenantId,
    queryFn: () => fetchDisp({ data: { tenantId: tenantId! } }),
  });

  const [rCode, setRCode] = useState("");
  const [rLabel, setRLabel] = useState("");
  const [dCode, setDCode] = useState("");
  const [dLabel, setDLabel] = useState("");
  const [dInspect, setDInspect] = useState(false);

  async function addReason() {
    if (!tenantId || !rCode.trim() || !rLabel.trim()) return;
    try {
      await saveReason({ data: { tenantId, code: rCode.trim(), label: rLabel.trim(), active: true } });
      setRCode(""); setRLabel("");
      qc.invalidateQueries({ queryKey: ["return-reasons", tenantId] });
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed"); }
  }
  async function addDisp() {
    if (!tenantId || !dCode.trim() || !dLabel.trim()) return;
    try {
      await saveDisp({ data: {
        tenantId, code: dCode.trim(), label: dLabel.trim(),
        requiresInspection: dInspect, active: true,
      } });
      setDCode(""); setDLabel(""); setDInspect(false);
      qc.invalidateQueries({ queryKey: ["return-dispositions", tenantId] });
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed"); }
  }
  async function del(kind: "reason" | "disp", id: string) {
    try {
      if (kind === "reason") await removeReason({ data: { id } });
      else await removeDisp({ data: { id } });
      qc.invalidateQueries({
        queryKey: [kind === "reason" ? "return-reasons" : "return-dispositions", tenantId],
      });
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed"); }
  }

  if (!tenantId) {
    return (
      <div className="p-6">
        <PageHeader title="Returns Config" />
        <Card className="p-6 text-sm text-muted-foreground">Select a tenant.</Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
      <PageHeader
        title="Returns Config"
        description="Return reasons and dispositions used across every RMA. Required before warehouse staff can process a return."
      />

      <Card className="p-5 space-y-4">
        <div className="flex items-baseline justify-between">
          <h3 className="font-medium text-sm">Return Reasons</h3>
          <span className="text-xs text-muted-foreground">
            {reasonsQ.data?.reasons.length ?? 0} configured
          </span>
        </div>
        <div className="grid sm:grid-cols-[160px_1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Code</Label>
            <Input value={rCode} onChange={(e) => setRCode(e.target.value)} placeholder="damaged" maxLength={60} />
          </div>
          <div>
            <Label className="text-xs">Label shown to customer</Label>
            <Input value={rLabel} onChange={(e) => setRLabel(e.target.value)} placeholder="Arrived damaged" maxLength={120} />
          </div>
          <Button onClick={addReason} disabled={!rCode.trim() || !rLabel.trim()}>
            <Plus className="h-4 w-4 mr-1" />Add
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Code</TableHead>
              <TableHead>Label</TableHead>
              <TableHead className="w-20">Active</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(reasonsQ.data?.reasons ?? []).map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell>{r.label}</TableCell>
                <TableCell>{r.active ? <Badge variant="outline">Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => del("reason", r.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-baseline justify-between">
          <h3 className="font-medium text-sm">Dispositions</h3>
          <span className="text-xs text-muted-foreground">
            {dispQ.data?.dispositions.length ?? 0} configured
          </span>
        </div>
        <div className="grid sm:grid-cols-[160px_1fr_auto_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Code</Label>
            <Input value={dCode} onChange={(e) => setDCode(e.target.value)} placeholder="restock_a" maxLength={60} />
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input value={dLabel} onChange={(e) => setDLabel(e.target.value)} placeholder="Restock as A-grade" maxLength={120} />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={dInspect} onCheckedChange={setDInspect} id="ins" />
            <Label htmlFor="ins" className="text-xs">Inspect</Label>
          </div>
          <Button onClick={addDisp} disabled={!dCode.trim() || !dLabel.trim()}>
            <Plus className="h-4 w-4 mr-1" />Add
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Code</TableHead>
              <TableHead>Label</TableHead>
              <TableHead className="w-24">Inspect</TableHead>
              <TableHead className="w-20">Active</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(dispQ.data?.dispositions ?? []).map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-mono text-xs">{d.code}</TableCell>
                <TableCell>{d.label}</TableCell>
                <TableCell>{d.requires_inspection ? "Yes" : "No"}</TableCell>
                <TableCell>{d.active ? <Badge variant="outline">Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => del("disp", d.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}